import { ExportError, errorMessage } from '../app/errors';
import { log } from '../app/logger';

/**
 * Getting a measurement off the phone — the escape routes, shared by every harness.
 *
 * The scenario this is written for: Andrew is standing at a touchline, the phone is the only device
 * present, and the number he just took cannot be reproduced at a desk. So there are three routes,
 * tried in order of convenience, and **every one of them fails loudly**. A silent share failure at
 * a venue means the measurement is gone.
 *
 *   1. Web Share with a JSON file attachment (CARD-2's mechanism, reused)
 *   2. Web Share with the summary as text, when file sharing is refused
 *   3. Download, and clipboard copy, both always available on screen
 *
 * ENV-4: no telemetry, no analytics, no third-party runtime scripts. Nothing here posts anywhere;
 * every route hands the bytes to the operating system's own share sheet or file system.
 *
 * BUILD-UNIT NOTE: extracted by U-1 from U-0's `exportResults.ts`, unchanged in behavior. Two
 * harnesses now export, and a second copy of this logic is a second place for the loud-failure
 * property to quietly stop being true.
 */

const MODULE = 'export';

export interface Artifact {
  /** The full record, for reanalysis. */
  json: string;
  /** The human summary, for pasting into a message. */
  text: string;
  filename: string;
  title: string;
}

export type ExportRoute = 'share-file' | 'share-text' | 'download';

/**
 * @throws {ExportError} when every route fails, and when the user cancels the share sheet — a
 *   cancellation is reported as a cancellation, never silently converted into a download the user
 *   did not ask for.
 */
export async function shareArtifact(artifact: Artifact): Promise<ExportRoute> {
  const attempts: string[] = [];

  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };

  if (typeof nav.share === 'function') {
    try {
      const file = new File([artifact.json], artifact.filename, { type: 'application/json' });
      if (!nav.canShare || nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: artifact.title });
        log.info(MODULE, 'shared results as a file', { filename: artifact.filename });
        return 'share-file';
      }
      attempts.push('navigator.canShare rejected a file attachment');
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ExportError('Share cancelled. Nothing was exported.', { cause });
      }
      attempts.push(`file share failed: ${errorMessage(cause)}`);
    }

    try {
      await nav.share({ title: artifact.title, text: artifact.text });
      log.info(MODULE, 'shared results as text');
      return 'share-text';
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ExportError('Share cancelled. Nothing was exported.', { cause });
      }
      attempts.push(`text share failed: ${errorMessage(cause)}`);
    }
  } else {
    attempts.push('navigator.share is unavailable');
  }

  try {
    downloadArtifact(artifact);
    return 'download';
  } catch (cause) {
    attempts.push(`download failed: ${errorMessage(cause)}`);
  }

  throw new ExportError(
    `Could not export the results. Copy them from the screen before leaving this page. Tried: ${attempts.join('; ')}`,
  );
}

/** @throws {ExportError} if the object URL or anchor click fails. */
export function downloadArtifact(artifact: Artifact): void {
  try {
    const blob = new Blob([artifact.json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = artifact.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoked on the next task so Safari has issued the download before the URL disappears.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    log.info(MODULE, 'downloaded results', { filename: artifact.filename });
  } catch (cause) {
    throw new ExportError('Download failed.', { cause });
  }
}

/** @throws {ExportError} when the clipboard is unavailable or refuses. */
export async function copyArtifactText(artifact: Artifact): Promise<void> {
  try {
    await navigator.clipboard.writeText(artifact.text);
    log.info(MODULE, 'copied results to clipboard');
  } catch (cause) {
    throw new ExportError('Clipboard copy failed. Read the numbers off the screen instead.', {
      cause,
    });
  }
}

/** A filesystem-safe timestamped name. */
export function stampedFilename(prefix: string, at: number): string {
  return `${prefix}-${new Date(at).toISOString().replace(/[:.]/g, '-')}.json`;
}
